import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismIcon, PrismInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { local } from 'embroider-css-modules';
import type { CentralMappingCategory } from 'lumen/models';
import { localCopy } from 'tracked-toolbox';

import styles from './category.module.css';

interface SettingsCentralMappingCategoriesCategorySignature {
  Args: {
    addChildCategory: (category: CentralMappingCategory) => void;
    category: CentralMappingCategory;
    deleteCategory: (category: CentralMappingCategory) => void;
    deleteCategoryWithConfirmationModal: (
      category: CentralMappingCategory,
    ) => void;
    isReadOnly?: boolean;
    isSelected: boolean;
    maxDepth: number;
    onClick: (category: CentralMappingCategory) => void;
    selectedCategories: CentralMappingCategory[];
    trackCategoryAdded?: (
      level: number,
      eventTrigger: 'new-item-button' | 'add-level-button',
    ) => void;
    trackCategoryRenamed?: (level: number, hasSubCategories: boolean) => void;
  };
}

export default class SettingsCentralMappingCategoriesCategory extends Component<SettingsCentralMappingCategoriesCategorySignature> {
  @service declare intl: Services['intl'];

  @tracked isEditingManually = false;
  @tracked isHovering = false;

  MAX_LENGTH_CATEGORY_NAME = 50;

  @localCopy('args.category.name') declare newCategoryName: string;

  get chevronIconColor(): 'inherit' | 'default-inverse' {
    const { category, selectedCategories } = this.args;

    // Check if it's the last category in the array
    if (selectedCategories[selectedCategories.length - 1] === category) {
      return 'default-inverse';
    }

    return 'inherit';
  }

  get classesForContainer(): string[] {
    const { category, selectedCategories } = this.args;
    const { isHovering } = this;

    const classes: string[] = ['container'];

    if (isHovering) {
      classes.push('is-hovering');
    }

    // Check if it's the last category in the array
    if (selectedCategories[selectedCategories.length - 1] === category) {
      classes.push('is-active');
    } else if (selectedCategories.includes(category)) {
      classes.push('is-active-group');
    }

    return classes;
  }

  get errorMessageCategoryName(): string {
    if (this.isNewCategoryNameTooLong) {
      return this.intl.t(
        'central_mapping.hierarchy.category.error.name_too_long',
      );
    }

    return this.intl.t(
      'central_mapping.hierarchy.category.error.name_already_in_use',
    );
  }

  get hasErrorsCategoryName(): boolean {
    return !this.isNewCategoryNameUnique || this.isNewCategoryNameTooLong;
  }

  get isEditing(): boolean {
    return this.isEditingManually || !this.args.category.hasConfirmedName;
  }

  get isNewCategoryNameFilled(): boolean {
    return this.newCategoryNameTrimmed.length > 0;
  }

  get isNewCategoryNameTooLong(): boolean {
    return this.newCategoryNameTrimmed.length > this.MAX_LENGTH_CATEGORY_NAME;
  }

  get isNewCategoryNameUnique(): boolean {
    const { newCategoryNameTrimmed } = this;
    const { category } = this.args;

    if (!category.parent) {
      return true;
    }

    return category.parent.undeletedChildren.every((child) => {
      // Don't check on the category itself.
      if (child === category) {
        return true;
      }

      // Check that no siblings have the same name.
      if (child.name.toLowerCase() !== newCategoryNameTrimmed.toLowerCase()) {
        return true;
      }

      return false;
    });
  }

  get isNewCategoryNameValid(): boolean {
    return (
      this.isNewCategoryNameUnique &&
      this.isNewCategoryNameFilled &&
      !this.isNewCategoryNameTooLong
    );
  }

  get newCategoryNameTrimmed(): string {
    return this.newCategoryName.trim();
  }

  get showAddChildCategoryButton(): boolean {
    const { category, maxDepth } = this.args;
    return !category.hasChildren && category.depth < maxDepth;
  }

  get showCategoryActionButtons(): boolean {
    return this.args.category.parent !== null || !this.args.category.isFixed;
  }

  get showHoverActions(): boolean {
    return this.isHovering && !this.args.isReadOnly;
  }

  @action
  onClick(): void {
    const { onClick, category } = this.args;

    if (this.isEditing) {
      return;
    }

    onClick(category);
  }

  @action
  onClickAddChildCategory(event: Event): void {
    const { addChildCategory, category } = this.args;

    event.stopPropagation();
    addChildCategory(category);
  }

  @action
  onClickDeleteCategory(event: Event): void {
    const { deleteCategoryWithConfirmationModal, category } = this.args;

    event.stopPropagation();
    deleteCategoryWithConfirmationModal(category);
  }

  @action
  onClickEditCategoryName(event: Event): void {
    event.stopPropagation();
    this.newCategoryName = this.args.category.name;
    this.isEditingManually = true;
  }

  @action
  onInputCategoryName(event: Event): void {
    this.newCategoryName = (event.target as HTMLInputElement)?.value;
  }

  @action
  onKeyDownCategoryName(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.updateCategoryName();
    }
  }

  @action
  onMouseEnter(): void {
    if (this.isEditing) {
      return;
    }

    this.isHovering = true;
  }

  @action
  onMouseLeave(): void {
    if (this.isEditing) {
      return;
    }

    this.isHovering = false;
  }

  @action
  updateCategoryName(): void {
    // Fix for `onKeyDownCategoryName`: when pressing Enter, `updateCategoryName` is called.
    // However, the `onBlur` of the input also triggers, which also calls `updateCategoryName`.
    // To avoid it being executed twice and Ember complaining about the `isEditingManually` property
    // being changed twice in the same run loop, we return early if the `updateCategoryName` has already been called.
    if (!this.isEditing) {
      return;
    }

    const {
      category,
      deleteCategory,
      trackCategoryAdded,
      trackCategoryRenamed,
    } = this.args;

    if (!this.isNewCategoryNameValid) {
      if (!category.hasConfirmedName) {
        deleteCategory(category);
        return;
      }

      this.newCategoryName = category.name;
    }

    if (category.hasConfirmedName) {
      // Track the event before updating `category.name`, otherwise `category.hasConfirmedName` has been adjusted already.
      // We use `category.hasConfirmedName` to check whether the category is newly created or not.
      trackCategoryRenamed?.(category.depth + 1, category.hasChildren);
    } else {
      const usageAnalyticsEventTrigger =
        category.parent && [...category.parent.children].length > 1
          ? 'new-item-button'
          : 'add-level-button';

      trackCategoryAdded?.(category.depth + 1, usageAnalyticsEventTrigger);
    }

    category.name = this.newCategoryNameTrimmed;

    this.isEditingManually = false;
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class={{local styles this.classesForContainer}}
      data-test-id="central-mapping-hierarchy-category"
      role="button"
      title={{unless this.isEditing @category.name}}
      {{on "click" this.onClick}}
      {{on "mouseenter" this.onMouseEnter}}
      {{on "mouseleave" this.onMouseLeave}}
    >
      {{#if this.isEditing}}
        <PrismInput
          @autofocus={{true}}
          @errorMessage={{this.errorMessageCategoryName}}
          @hasErrors={{this.hasErrorsCategoryName}}
          {{! isTouched is needed for the error state }}
          @isTouched={{true}}
          @maxLength={{this.MAX_LENGTH_CATEGORY_NAME}}
          @onBlur={{this.updateCategoryName}}
          @onInput={{this.onInputCategoryName}}
          @onKeyDown={{this.onKeyDownCategoryName}}
          @removePadding={{true}}
          @type="text"
          @value={{this.newCategoryName}}
          data-test-id="central-mapping-hierarchy-category-input"
        />
      {{else}}
        <div
          class="my-100 mr-100 ml-200 flex h-full w-full flex-row items-center justify-between gap-200 overflow-hidden"
        >
          <div
            class={{concat
              styles.category-name
              " "
              "text-300--regular truncate"
            }}
            data-test-id="central-mapping-hierarchy-category-name"
          >
            {{@category.name}}
          </div>

          <div class="flex flex-row items-center gap-100">
            {{#if this.showHoverActions}}
              <div class="flex flex-row items-center gap-100">
                {{#if this.showCategoryActionButtons}}
                  <PrismButton
                    @buttonType="ghost"
                    @icon="edit-pencil"
                    @size="small"
                    data-test-id="central-mapping-hierarchy-category-rename"
                    {{on "click" this.onClickEditCategoryName}}
                  />
                  <PrismButton
                    @buttonType="ghost"
                    @icon="bin"
                    @size="small"
                    data-test-id="central-mapping-hierarchy-category-delete"
                    {{on "click" this.onClickDeleteCategory}}
                  />
                {{/if}}

                {{#if this.showAddChildCategoryButton}}
                  <PrismButton
                    @buttonType="secondary"
                    @iconBefore="plus"
                    @isDisabled={{this.isEditing}}
                    @label={{t "settings.central_mapping.category.add_level"}}
                    @size="small"
                    data-test-id="central-mapping-hierarchy-category-add-level"
                    {{on "click" this.onClickAddChildCategory}}
                  />
                {{/if}}
              </div>
            {{/if}}

            {{#if @category.hasChildren}}
              <PrismIcon
                @color={{this.chevronIconColor}}
                @name="chevron-right"
              />
            {{/if}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
